import { motion } from 'framer-motion';
import React, { useCallback, useEffect, useState } from 'react';
import {
    FiCalendar,
    FiEdit3,
    FiEye,
    FiFilter,
    FiMail,
    FiPhone,
    FiRefreshCw,
    FiSearch,
    FiStar,
    FiTrash2,
    FiTrendingUp,
    FiUser,
    FiUsers,
    FiX
} from 'react-icons/fi';
import apiService from '../services/api';

const ClientIntakeForms = () => {
  const [intakeForms, setIntakeForms] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Pagination & Filtering
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalForms, setTotalForms] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    priority: 'all',
    industry: 'all',
    businessSize: 'all',
    businessPhase: 'all'
  });
  
  // Modals
  const [selectedForm, setSelectedForm] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteFormId, setDeleteFormId] = useState(null);

  // Data fetching
  const fetchIntakeForms = async (isRefresh = false) => {
    try {
      if (!isRefresh) setLoading(true);
      setError('');

      const params = {
        page: currentPage,
        limit: 10,
        ...filters
      };

      const response = await apiService.getClientIntakeForms(params);
      
      setIntakeForms(response.data.intakeForms);
      setCurrentPage(response.data.currentPage);
      setTotalPages(response.data.totalPages);
      setTotalForms(response.data.totalForms);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching client intake forms:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiService.getClientIntakeStats();
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  // Event handlers
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleStatusUpdate = async (formId, newStatus) => {
    try {
      await apiService.updateClientIntakeStatus(formId, { status: newStatus });
      await fetchIntakeForms(true);
      setShowEditModal(false);
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const handleDeleteForm = async () => {
    try {
      await apiService.deleteClientIntakeForm(deleteFormId);
      await fetchIntakeForms(true);
      setShowDeleteModal(false);
      setDeleteFormId(null);
    } catch (err) {
      console.error('Error deleting form:', err);
    }
  };

  // Utility functions
  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-blue-100 text-blue-800',
      'reviewing': 'bg-yellow-100 text-yellow-800',
      'in-consultation': 'bg-purple-100 text-purple-800',
      'proposal-sent': 'bg-indigo-100 text-indigo-800',
      'closed': 'bg-green-100 text-green-800',
      'on-hold': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'new': 'New',
      'reviewing': 'Reviewing',
      'in-consultation': 'In Consultation',
      'proposal-sent': 'Proposal Sent',
      'closed': 'Closed',
      'on-hold': 'On Hold'
    };
    return labels[status] || status;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'low': 'text-green-600',
      'medium': 'text-yellow-600',
      'high': 'text-orange-600',
      'urgent': 'text-red-600'
    };
    return colors[priority] || 'text-gray-600';
  };

  const renderStars = (score = 0) => {
    const stars = [];
    const maxStars = 10;
    const filledStars = Math.round(score);
    
    for (let i = 0; i < maxStars; i++) {
      stars.push(
        <FiStar
          key={i}
          size={12}
          className={i < filledStars ? 'text-yellow-400 fill-current' : 'text-gray-300'}
        />
      );
    }
    return stars;
  };

  // Effects
  useEffect(() => {
    fetchIntakeForms();
    fetchStats();
  }, [currentPage, filters]);

  const debounce = useCallback((func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }, []);

  const debouncedSearch = useCallback(
    debounce((searchValue) => {
      handleFilterChange('search', searchValue);
    }, 500),
    []
  );

  if (loading && intakeForms.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <span className="text-white text-lg">Loading client intake forms...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black relative">
      {/* Simplified Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-10"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl opacity-10"></div>
      </div>

      <div className="relative z-10 p-6 space-y-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="relative">
          <div className="bg-gradient-to-r from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-white via-cyan-200 to-purple-200 bg-clip-text text-transparent font-display mb-2">
                  Client Intake Forms
                </h1>
                <p className="text-slate-300 text-lg">Manage and track client intake form submissions</p>
              </div>
              <motion.button
                onClick={() => fetchIntakeForms(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-400/30 rounded-lg text-cyan-400 text-sm transition-all duration-300 flex items-center gap-2"
              >
                <FiRefreshCw size={16} />
                <span>Refresh Data</span>
              </motion.button>
            </div>

            {error && (
              <div className="mt-4 p-4 bg-red-500/20 border border-red-400/30 rounded-lg flex items-center text-red-400">
                <span>Error loading data: {error}</span>
              </div>
            )}
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 rounded-xl bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 shadow-lg">
                <FiUsers className="w-6 h-6 text-white" />
              </div>
            </div>
            <div className="space-y-2">
              <h3 className="text-slate-300 text-sm font-medium">Total Forms</h3>
              <div className="text-3xl font-bold text-white">
                {stats.general?.totalForms || 0}
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 rounded-xl bg-gradient-to-r from-emerald-400 via-teal-500 to-green-600 shadow-lg">
                <FiTrendingUp className="w-6 h-6 text-white" />
              </div>
              <div className="flex items-center text-sm font-medium px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-400/30">
                <span>New</span>
              </div>
            </div>
            <div className="space-y-2">
              <h3 className="text-slate-300 text-sm font-medium">New Forms</h3>
              <div className="text-3xl font-bold text-white">
                {stats.general?.totalNew || 0}
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 rounded-xl bg-gradient-to-r from-violet-400 via-purple-500 to-pink-600 shadow-lg">
                <FiEdit3 className="w-6 h-6 text-white" />
              </div>
              <div className="flex items-center text-sm font-medium px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-400 border border-yellow-400/30">
                <span>Active</span>
              </div>
            </div>
            <div className="space-y-2">
              <h3 className="text-slate-300 text-sm font-medium">In Progress</h3>
              <div className="text-3xl font-bold text-white">
                {(stats.general?.totalReviewing || 0) + (stats.general?.totalInConsultation || 0)}
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 rounded-xl bg-gradient-to-r from-amber-400 via-orange-500 to-red-600 shadow-lg">
                <FiStar className="w-6 h-6 text-white" />
              </div>
              <div className="flex items-center text-sm font-medium px-3 py-1 rounded-full bg-green-500/20 text-green-400 border border-green-400/30">
                <span>Done</span>
              </div>
            </div>
            <div className="space-y-2">
              <h3 className="text-slate-300 text-sm font-medium">Closed</h3>
              <div className="text-3xl font-bold text-white">
                {stats.general?.totalClosed || 0}
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
          <div className="flex items-center space-x-2 mb-6">
            <FiFilter className="text-cyan-400" size={20} />
            <h3 className="text-xl font-bold text-white">Filters</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                <input
                  type="text"
                  placeholder="Search forms..."
                  className="w-full pl-10 pr-4 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-white placeholder-slate-400"
                  onChange={(e) => debouncedSearch(e.target.value)}
                />
              </div>
            </div>

            {/* Status Filter */}
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="bg-slate-700/50 border border-slate-600/50 rounded-lg px-3 py-2 focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-white"
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="reviewing">Reviewing</option>
              <option value="in-consultation">In Consultation</option>
              <option value="proposal-sent">Proposal Sent</option>
              <option value="closed">Closed</option>
              <option value="on-hold">On Hold</option>
            </select>

            {/* Priority Filter */}
            <select
              value={filters.priority}
              onChange={(e) => handleFilterChange('priority', e.target.value)}
              className="bg-slate-700/50 border border-slate-600/50 rounded-lg px-3 py-2 focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-white"
            >
              <option value="all">All Priority</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>

            {/* Industry Filter */}
            <select
              value={filters.industry}
              onChange={(e) => handleFilterChange('industry', e.target.value)}
              className="bg-slate-700/50 border border-slate-600/50 rounded-lg px-3 py-2 focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-white"
            >
              <option value="all">All Industries</option>
              <option value="Technology">Technology</option>
              <option value="Healthcare">Healthcare</option>
              <option value="Financial Services">Financial Services</option>
              <option value="Life Sciences">Life Sciences</option>
              <option value="Manufacturing">Manufacturing</option>
              <option value="Retail/E-commerce">Retail/E-commerce</option>
              <option value="Education">Education</option>
              <option value="Government">Government</option>
              <option value="Other">Other</option>
            </select>

            {/* Business Size Filter */}
            <select
              value={filters.businessSize}
              onChange={(e) => handleFilterChange('businessSize', e.target.value)}
              className="bg-slate-700/50 border border-slate-600/50 rounded-lg px-3 py-2 focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-white"
            >
              <option value="all">All Sizes</option>
              <option value="Startup (1-10 employees)">Startup</option>
              <option value="Small Business (11-50 employees)">Small Business</option>
              <option value="Medium Business (51-250 employees)">Medium Business</option>
              <option value="Large Enterprise (250+ employees)">Large Enterprise</option>
            </select>
          </div>
        </div>

        {/* Forms Table */}
        <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-700/30">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Company & Industry
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Business Phase
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Value Score
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/30">
                {intakeForms.map((form, index) => (
                  <motion.tr
                    key={form._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-slate-700/20 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                          {form.fullName.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-white">{form.fullName}</div>
                          <div className="text-sm text-slate-400 flex items-center space-x-2">
                            <FiMail size={12} />
                            <span>{form.email}</span>
                          </div>
                          {form.phone && (
                            <div className="text-sm text-slate-400 flex items-center space-x-2">
                              <FiPhone size={12} />
                              <span>{form.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-white">{form.companyName}</div>
                      <div className="text-sm text-slate-400">{form.industry}</div>
                      <div className="text-xs text-slate-500">{form.businessSize}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-slate-300">{form.businessPhase}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-1">
                        {renderStars(form.projectValueScore)}
                        <span className="text-xs text-slate-400 ml-2">
                          {form.projectValueScore?.toFixed(1) || '0.0'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(form.status)}`}>
                        {getStatusLabel(form.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-medium capitalize ${getPriorityColor(form.priority)}`}>
                        {form.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                      <div className="flex items-center space-x-1">
                        <FiCalendar size={12} />
                        <span>{new Date(form.createdAt).toLocaleDateString()}</span>
                      </div>
                      <div className="text-xs text-slate-500">
                        {new Date(form.createdAt).toLocaleTimeString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => {
                            setSelectedForm(form);
                            setShowViewModal(true);
                          }}
                          className="text-cyan-400 hover:text-cyan-300 transition-colors"
                          title="View Details"
                        >
                          <FiEye size={16} />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedForm(form);
                            setShowEditModal(true);
                          }}
                          className="text-emerald-400 hover:text-emerald-300 transition-colors"
                          title="Edit Status"
                        >
                          <FiEdit3 size={16} />
                        </button>
                        <button
                          onClick={() => {
                            setDeleteFormId(form._id);
                            setShowDeleteModal(true);
                          }}
                          className="text-red-400 hover:text-red-300 transition-colors"
                          title="Delete"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-slate-700/20 px-4 py-3 flex items-center justify-between border-t border-slate-600/30 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-300 bg-slate-700/50 hover:bg-slate-600/50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-300 bg-slate-700/50 hover:bg-slate-600/50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-slate-300">
                    Showing <span className="font-medium">{((currentPage - 1) * 10) + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(currentPage * 10, totalForms)}</span> of{' '}
                    <span className="font-medium">{totalForms}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-slate-600 bg-slate-700/50 text-sm font-medium text-slate-300 hover:bg-slate-600/50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    {[...Array(totalPages)].map((_, i) => (
                      <button
                        key={i + 1}
                        onClick={() => setCurrentPage(i + 1)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === i + 1
                            ? 'z-10 bg-cyan-500/20 border-cyan-400/30 text-cyan-400'
                            : 'bg-slate-700/50 border-slate-600 text-slate-300 hover:bg-slate-600/50'
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-slate-600 bg-slate-700/50 text-sm font-medium text-slate-300 hover:bg-slate-600/50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* View Modal */}
        {showViewModal && selectedForm && (
          <ViewFormModal
            form={selectedForm}
            onClose={() => {
              setShowViewModal(false);
              setSelectedForm(null);
            }}
          />
        )}

        {/* Edit Modal */}
        {showEditModal && selectedForm && (
          <EditFormModal
            form={selectedForm}
            onClose={() => {
              setShowEditModal(false);
              setSelectedForm(null);
            }}
            onUpdate={handleStatusUpdate}
          />
        )}

        {/* Delete Modal */}
        {showDeleteModal && (
          <DeleteModal
            title="Delete Client Intake Form"
            message="Are you sure you want to delete this client intake form? This action cannot be undone."
            onClose={() => {
              setShowDeleteModal(false);
              setDeleteFormId(null);
            }}
            onConfirm={handleDeleteForm}
          />
        )}
      </div>
    </div>
  );
};

// View Form Modal Component
const ViewFormModal = ({ form, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-slate-800/95 via-gray-800/95 to-slate-800/95 backdrop-blur-xl rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-white/10 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-600/30">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-white via-cyan-200 to-purple-200 bg-clip-text text-transparent">Client Intake Form Details</h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
              <FiUser className="text-cyan-400" />
              <span>Contact Information</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-700/30 p-4 rounded-lg border border-slate-600/30">
              <div className="text-slate-300"><strong className="text-white">Name:</strong> {form.fullName}</div>
              <div className="text-slate-300"><strong className="text-white">Email:</strong> {form.email}</div>
              <div className="text-slate-300"><strong className="text-white">Phone:</strong> {form.phone || 'Not provided'}</div>
              <div className="text-slate-300"><strong className="text-white">Company:</strong> {form.companyName}</div>
              <div className="text-slate-300"><strong className="text-white">Industry:</strong> {form.industry}</div>
              <div className="text-slate-300"><strong className="text-white">Business Size:</strong> {form.businessSize}</div>
              <div className="text-slate-300"><strong className="text-white">Preferred Contact:</strong> {form.preferredContact}</div>
            </div>
          </div>

          {/* Business Goals */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
              <FiTrendingUp className="text-emerald-400" />
              <span>Business Goals & Strategic Objectives</span>
            </h3>
            <div className="bg-slate-700/30 p-4 rounded-lg space-y-4 border border-slate-600/30">
              <div className="text-slate-300"><strong className="text-white">Business Phase:</strong> {form.businessPhase}</div>
              <div>
                <strong className="text-white">Strategic Goals:</strong>
                <p className="mt-2 p-3 bg-slate-600/50 rounded border border-slate-500/30 text-slate-200">{form.strategicGoals}</p>
              </div>
              {form.servicesNeeded && form.servicesNeeded.length > 0 && (
                <div>
                  <strong className="text-white">Services Needed:</strong>
                  <ul className="mt-2 space-y-1">
                    {form.servicesNeeded.map((service, index) => (
                      <li key={index} className="flex items-center space-x-2 text-slate-200">
                        <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                        <span>{service}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Technology Landscape */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Technology Landscape</h3>
            <div className="bg-slate-700/30 p-4 rounded-lg space-y-4 border border-slate-600/30">
              {form.currentTools && (
                <div>
                  <strong className="text-white">Current Tools:</strong>
                  <p className="mt-2 p-3 bg-slate-600/50 rounded border border-slate-500/30 text-slate-200">{form.currentTools}</p>
                </div>
              )}
              {form.preferredTech && (
                <div>
                  <strong className="text-white">Preferred Technology:</strong>
                  <p className="mt-2 p-3 bg-slate-600/50 rounded border border-slate-500/30 text-slate-200">{form.preferredTech}</p>
                </div>
              )}
              {form.integrationSystems && (
                <div>
                  <strong className="text-white">Integration Systems:</strong>
                  <p className="mt-2 p-3 bg-slate-600/50 rounded border border-slate-500/30 text-slate-200">{form.integrationSystems}</p>
                </div>
              )}
            </div>
          </div>

          {/* Timeline & Success */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Timeline & Success Criteria</h3>
            <div className="bg-slate-700/30 p-4 rounded-lg space-y-4 border border-slate-600/30">
              <div className="text-slate-300"><strong className="text-white">Desired Start Date:</strong> {form.startDate}</div>
              {form.launchDate && <div className="text-slate-300"><strong className="text-white">Launch Date:</strong> {form.launchDate}</div>}
              <div>
                <strong className="text-white">Success Definition:</strong>
                <p className="mt-2 p-3 bg-slate-600/50 rounded border border-slate-500/30 text-slate-200">{form.successDefinition}</p>
              </div>
              {form.keyKPIs && (
                <div>
                  <strong className="text-white">Key KPIs:</strong>
                  <p className="mt-2 p-3 bg-slate-600/50 rounded border border-slate-500/30 text-slate-200">{form.keyKPIs}</p>
                </div>
              )}
            </div>
          </div>

          {/* Value Add Interests */}
          {form.valueAddInterests && form.valueAddInterests.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Additional Interests</h3>
              <div className="bg-slate-700/30 p-4 rounded-lg border border-slate-600/30">
                <ul className="space-y-1">
                  {form.valueAddInterests.map((interest, index) => (
                    <li key={index} className="flex items-center space-x-2 text-slate-200">
                      <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                      <span>{interest}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Metadata */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Form Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-700/30 p-4 rounded-lg text-sm border border-slate-600/30">
              <div className="text-slate-300"><strong className="text-white">Status:</strong> {form.status}</div>
              <div className="text-slate-300"><strong className="text-white">Priority:</strong> {form.priority}</div>
              <div className="text-slate-300"><strong className="text-white">Project Value Score:</strong> {form.projectValueScore?.toFixed(1) || 'N/A'}</div>
              <div className="text-slate-300"><strong className="text-white">Submitted:</strong> {new Date(form.createdAt).toLocaleDateString()}</div>
              {form.assignedTo && <div className="text-slate-300"><strong className="text-white">Assigned To:</strong> {form.assignedTo}</div>}
              {form.estimatedBudget && <div className="text-slate-300"><strong className="text-white">Estimated Budget:</strong> {form.estimatedBudget}</div>}
              {form.estimatedTimeline && <div className="text-slate-300"><strong className="text-white">Estimated Timeline:</strong> {form.estimatedTimeline}</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Edit Form Modal Component
const EditFormModal = ({ form, onClose, onUpdate }) => {
  const [status, setStatus] = useState(form.status);
  const [assignedTo, setAssignedTo] = useState(form.assignedTo || '');
  const [estimatedBudget, setEstimatedBudget] = useState(form.estimatedBudget || '');
  const [estimatedTimeline, setEstimatedTimeline] = useState(form.estimatedTimeline || '');
  const [notes, setNotes] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdate(form._id, status, {
      assignedTo,
      estimatedBudget,
      estimatedTimeline,
      notes: notes.trim() ? notes : undefined
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-slate-800/95 via-gray-800/95 to-slate-800/95 backdrop-blur-xl rounded-xl max-w-md w-full border border-white/10 shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-slate-600/30">
          <h2 className="text-xl font-bold text-white">Update Form Status</h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <FiX size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full bg-slate-700/50 border border-slate-600/50 rounded-lg px-3 py-2 focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-white"
            >
              <option value="new">New</option>
              <option value="reviewing">Reviewing</option>
              <option value="in-consultation">In Consultation</option>
              <option value="proposal-sent">Proposal Sent</option>
              <option value="closed">Closed</option>
              <option value="on-hold">On Hold</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Assigned To</label>
            <input
              type="text"
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              className="w-full bg-slate-700/50 border border-slate-600/50 rounded-lg px-3 py-2 focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-white placeholder-slate-400"
              placeholder="Enter assignee name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Estimated Budget</label>
            <select
              value={estimatedBudget}
              onChange={(e) => setEstimatedBudget(e.target.value)}
              className="w-full bg-slate-700/50 border border-slate-600/50 rounded-lg px-3 py-2 focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-white"
            >
              <option value="">Select budget range</option>
              <option value="Under $10K">Under $10K</option>
              <option value="$10K - $50K">$10K - $50K</option>
              <option value="$50K - $100K">$50K - $100K</option>
              <option value="$100K - $500K">$100K - $500K</option>
              <option value="$500K+">$500K+</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Estimated Timeline</label>
            <select
              value={estimatedTimeline}
              onChange={(e) => setEstimatedTimeline(e.target.value)}
              className="w-full bg-slate-700/50 border border-slate-600/50 rounded-lg px-3 py-2 focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-white"
            >
              <option value="">Select timeline</option>
              <option value="1-2 weeks">1-2 weeks</option>
              <option value="1 month">1 month</option>
              <option value="2-3 months">2-3 months</option>
              <option value="3-6 months">3-6 months</option>
              <option value="6+ months">6+ months</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Add Note</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full bg-slate-700/50 border border-slate-600/50 rounded-lg px-3 py-2 focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-white placeholder-slate-400"
              placeholder="Add a note about this update..."
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-700/50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-colors"
            >
              Update
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Delete Modal Component
const DeleteModal = ({ title, message, onClose, onConfirm }) => {
  const handleConfirm = async () => {
    await onConfirm();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-slate-800/95 via-gray-800/95 to-slate-800/95 backdrop-blur-xl rounded-xl max-w-md w-full border border-white/10 shadow-2xl">
        <div className="p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center border border-red-400/30">
              <FiTrash2 className="text-red-400" size={20} />
            </div>
            <h2 className="text-xl font-bold text-white">{title}</h2>
          </div>
          <p className="text-slate-300 mb-6">{message}</p>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-700/50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientIntakeForms; 